import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import api from '../utils/api';
import { PinIcon } from '../components/contacts/ContactsIcons';
import '../styles/MapPage.css';

// Leaflet is loaded on demand so it stays out of the initial bundle. The page
// shell still renders in the same frame as the click; only the basemap waits,
// and it was always going to wait on the network anyway.
const LeafletPointsMap = lazy(() => import('../components/LeafletPointsMap'));

function BackIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

const MapPage = () => {
  useTheme();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    const fetchMapContacts = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await api.get('/api/contact/mapContacts', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setConnections(Array.isArray(response.data) ? response.data : []);
        setStatus('ready');
      } catch (error) {
        console.error('Error fetching map contacts:', error);
        setStatus('error');
      }
    };

    fetchMapContacts();
  }, [navigate]);

  const points = useMemo(
    () =>
      connections
        .filter((conn) => conn.lat && conn.lng)
        .map((connection, index) => {
          const when = connection.dateAdded
            ? new Date(connection.dateAdded).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : null;
          return {
            id: connection._id || index,
            lat: connection.lat,
            lng: connection.lng,
            popup: when
              ? `${connection.name}\nMet ${when}`
              : connection.name || 'Connection',
          };
        }),
    [connections]
  );

  // Leaflet popups treat text as HTML; keep line breaks without dangerouslySetInnerHTML.
  const mapPoints = useMemo(
    () =>
      points.map((point) => ({
        ...point,
        popup: (
          <div className="map-page-popup">
            {String(point.popup)
              .split('\n')
              .map((line, lineIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={lineIndex}>{line}</div>
              ))}
          </div>
        ),
      })),
    [points]
  );

  const countLabel =
    points.length === 0
      ? 'No places yet'
      : points.length === 1
        ? '1 place'
        : `${points.length} places`;

  return (
    <div className="map-page">
      <header className="map-page-chrome" aria-label="Map controls">
        <button
          type="button"
          className="map-page-back"
          onClick={() => navigate('/contacts')}
        >
          <BackIcon />
          <span>Contacts</span>
        </button>

        <div className="map-page-meta" aria-live="polite">
          <PinIcon size={14} />
          <span>{status === 'loading' ? 'Loading…' : countLabel}</span>
        </div>
      </header>

      <div className="map-page-stage">
        {status === 'error' ? (
          <div className="map-page-state">
            <p>Couldn’t load your meeting places.</p>
            <button type="button" className="map-page-retry" onClick={() => window.location.reload()}>
              Try again
            </button>
          </div>
        ) : status === 'ready' && points.length === 0 ? (
          <div className="map-page-state">
            <PinIcon size={22} />
            <p>No locations captured yet.</p>
            <span>Places appear when both people allow location at the moment of the tap.</span>
          </div>
        ) : (
          <Suspense fallback={<div className="map-page-loading">Loading map…</div>}>
            <LeafletPointsMap
              center={[20, 0]}
              zoom={2}
              theme="dark"
              className="map-page-canvas"
              points={mapPoints}
              fitBounds={points.length > 0}
              markerVariant="dot"
              scrollWheelZoom
              zoomControl
            />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default MapPage;
